import { useState } from "react";
import { AlertTriangle, Check, Eye, EyeOff, GitCompare, Loader2, Undo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useEditor } from "@/components/editor/useEditorState";
import DiffDialog from "./DiffDialog";
import StopButton from "./StopButton";
import { RUNNING, usd } from "./useAiEdits";

const STATUS = { running: "In corso…", analyzing: "Analisi in corso…", analyzed: "Analisi pronta", ready: "Da confermare", applied: "Applicata",
  discarded: "Scartata", undone: "Annullata", error: "Errore", cancelled: "Interrotta" };
const TONE = { ready: "bg-sky-100 text-sky-800", applied: "bg-emerald-100 text-emerald-800", error: "bg-red-100 text-red-700", undone: "bg-slate-100 text-slate-600", discarded: "bg-slate-100 text-slate-500", cancelled: "bg-amber-100 text-amber-800" };

function Ops({ e, exclude, setExclude }) {
  const valid = e.ops.filter((o) => !o.error);
  const pick = e.status === "ready" && valid.length > 1;
  return (
    <ul className="mt-2 space-y-1" data-testid="ai-edit-ops">
      {e.ops.map((o) => (
        <li key={o.index} className="rounded border bg-white px-2 py-1.5 text-[11.5px] text-slate-700" data-testid={`ai-edit-op-${o.index}`}>
          <div className="flex items-start gap-1.5">
            {pick && !o.error && <Checkbox checked={!exclude.includes(o.index)} onCheckedChange={(v) => setExclude(v ? exclude.filter((x) => x !== o.index) : [...exclude, o.index])} className="mt-0.5" data-testid={`ai-edit-op-toggle-${o.index}`} />}
            <span className="min-w-0 flex-1">{o.label}{o.repaired && <span className="ml-1 text-[10px] text-emerald-700">(corretta automaticamente)</span>}</span>
          </div>
          {o.text_after != null && <p className="mt-1 text-[11px]"><span className="line-through text-slate-400">{o.text_before}</span> → <span className="text-slate-900">{o.text_after}</span></p>}
          {o.error && <p className="mt-1 text-[11px] text-red-600" data-testid={`ai-edit-op-error-${o.index}`}>Non applicabile: {o.error}</p>}
        </li>
      ))}
    </ul>
  );
}

export default function ProposalCard({ e, hook }) {
  const ed = useEditor();
  const [exclude, setExclude] = useState([]);
  const [diff, setDiff] = useState(false);
  const [force, setForce] = useState("");
  const previewing = ed.aiPreview?.id === e.id;
  const valid = e.ops.filter((o) => !o.error && !exclude.includes(o.index));
  const undo = async () => { const r = await hook.undo(e); if (r && r !== "ok") setForce(r); };
  if (RUNNING.includes(e.status)) {
    return (
      <div className="rounded-lg border bg-white p-3 text-sm text-slate-600 space-y-2" data-testid="ai-edit-running">
        <p className="flex items-center gap-2"><Loader2 size={14} className="shrink-0 animate-spin text-sky-600" />{e.label}: {STATUS[e.status]} (di solito 10-90 secondi)</p>
        <StopButton api={ed.api} projectId={ed.id} testid="ai-edit-stop-btn" />
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-white p-3" data-testid={`ai-edit-card-${e.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-[13px] font-semibold text-slate-900">{e.label}</p>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${TONE[e.status] || "bg-slate-100 text-slate-600"}`} data-testid="ai-edit-status">{STATUS[e.status]}</span>
      </div>
      <p className="mt-0.5 text-[10.5px] text-slate-500" title="Calcolato sui token usati × listino del provider: i crediti Universal Key effettivamente scalati possono essere diversi." data-testid="ai-edit-cost">Stima {usd(e.estimate?.usd)}{e.cost?.usd != null ? ` · a consuntivo (token × listino) ${usd(e.cost.usd)}` : ""}</p>
      {e.reply && <p className="mt-2 text-[12px] text-slate-700 whitespace-pre-wrap">{e.reply}</p>}
      {e.error && <p className={`mt-2 text-[12px] ${e.status === "cancelled" ? "text-amber-800" : "text-red-700"}`} data-testid="ai-edit-error"><AlertTriangle size={12} className="inline mr-1" />{e.error}</p>}
      {e.ops.length > 0 && <Ops e={e} exclude={exclude} setExclude={setExclude} />}
      {e.ignored?.length > 0 && <p className="mt-2 text-[11px] text-amber-700">Ignorate (fuori ambito o elementi bloccati): {e.ignored.join("; ")}</p>}
      {e.status === "ready" && (
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <Button size="sm" variant="outline" className="h-8" onClick={() => setDiff(true)} data-testid="ai-edit-diff-btn"><GitCompare size={13} className="mr-1" />Differenze</Button>
          <Button size="sm" variant="outline" className="h-8" disabled={!valid.length || !!hook.busy} onClick={() => (previewing ? ed.setAiPreview(null) : hook.preview(e, exclude))} data-testid="ai-edit-preview-btn">
            {previewing ? <EyeOff size={13} className="mr-1" /> : <Eye size={13} className="mr-1" />}{previewing ? "Chiudi" : "Anteprima"}
          </Button>
          <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" disabled={!valid.length || !!hook.busy} onClick={() => hook.apply(e, exclude)} data-testid="ai-edit-apply-btn"><Check size={13} className="mr-1" />Applica</Button>
          <Button size="sm" variant="outline" className="h-8" disabled={!!hook.busy} onClick={() => hook.discard(e)} data-testid="ai-edit-discard-btn"><X size={13} className="mr-1" />Scarta</Button>
        </div>
      )}
      {e.status === "applied" && (
        <div className="mt-3 space-y-1.5">
          <Button size="sm" variant="outline" className="h-8 w-full" disabled={!!hook.busy} onClick={undo} data-testid="ai-edit-undo-btn"><Undo2 size={13} className="mr-1" />Annulla modifica</Button>
          <p className="text-[10.5px] text-slate-500">La versione precedente è salvata: puoi ripristinarla anche da Versioni.</p>
        </div>
      )}
      {e.status !== "running" && <DiffDialog edit={e} open={diff} onOpenChange={setDiff} />}
      <ConfirmDialog open={!!force} onOpenChange={(o) => !o && setForce("")} title="Annullare comunque?" confirmLabel="Annulla comunque" destructive
        description={force} onConfirm={() => { setForce(""); hook.undo(e, true); }} />
    </div>
  );
}
